import { Check, CheckCircle2 } from "lucide-react";
import { ACCENT, H2 } from "@/components/ecommerce/shared";

export const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
export const FIELD =
  "bg-white/[0.05] border border-white/15 text-white placeholder:text-white/40 rounded-xl h-12 focus-visible:border-[#E8562A] focus-visible:ring-2 focus-visible:ring-[#E8562A]/30 transition-shadow";

export function Choice({ label, active, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={active}
      className={`relative text-left rounded-xl border px-4 py-3.5 pr-10 transition-[border-color,background-color] duration-200 ${
        active ? "border-[#E8562A] bg-[#E8562A]/12" : "border-white/12 bg-white/[0.03] hover:border-white/30 hover:bg-white/[0.06]"}`}>
      <span className={`text-sm font-semibold leading-snug ${active ? "text-white" : "text-white/75"}`}>{label}</span>
      <span className={`absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full flex items-center justify-center transition-[opacity,transform] ${active ? "opacity-100 scale-100" : "opacity-0 scale-75"}`} style={{ background: ACCENT }}>
        <Check className="h-3 w-3 text-white" strokeWidth={3} />
      </span>
    </button>
  );
}

export function Pill({ label, active, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={active}
      className={`rounded-full border px-4 py-2 text-sm font-semibold transition-[border-color,background-color,color] ${
        active ? "border-[#E8562A] text-white" : "border-white/15 text-white/70 bg-white/[0.03] hover:border-white/35"}`}
      style={active ? { background: ACCENT } : undefined}>
      {label}
    </button>
  );
}

export function Group({ legend, children, hint }) {
  return (
    <fieldset>
      <legend className="text-sm font-semibold text-white/85 mb-2.5">{legend}{hint && <span className="text-white/45 font-normal"> {hint}</span>}</legend>
      {children}
    </fieldset>
  );
}

export function Progress({ steps, step }) {
  return (
    <div className="flex items-center gap-2 mb-8" data-testid="ecom-form-progress">
      {steps.map((label, i) => (
        <div key={label} className="flex-1">
          <div className="h-1.5 rounded-full transition-colors duration-300" style={{ background: i <= step ? ACCENT : "rgba(255,255,255,0.12)" }} />
          <div className={`mt-2 text-[11px] font-semibold ${i === step ? "text-white" : "text-white/45"}`}>
            <span className="font-mono mr-1">{i + 1}.</span>{label}{i === step && <span className="sr-only"> (current step)</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

export function FormIntro() {
  return (
    <div className="text-center mb-10">
      <div className="text-[11px] tracking-[0.2em] uppercase font-bold mb-3" style={{ color: ACCENT }}>eCommerce quote · professional implementation</div>
      <H2 id="request-quote-heading" dark>Get Your Rekisa eCommerce Plan</H2>
      <p data-testid="ecom-form-intro" className="mt-5 text-white/70 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
        Tell us what your business wants to achieve online. Whether you need a new store, an eCommerce strategy or marketplace support,
        we'll review your requirements and recommend a practical way forward.
      </p>
    </div>
  );
}

export function FormSuccess() {
  return (
    <div data-testid="ecom-form-success" className="mx-auto max-w-2xl px-5 sm:px-8 text-center">
      <div className="h-16 w-16 rounded-full flex items-center justify-center mx-auto" style={{ background: `${ACCENT}22`, border: `1px solid ${ACCENT}66` }}>
        <CheckCircle2 className="h-8 w-8" style={{ color: ACCENT }} />
      </div>
      <h2 className="font-display mt-6 text-3xl sm:text-4xl font-black tracking-tight text-white">Thank you. Your eCommerce enquiry has been received.</h2>
      <p className="mt-4 text-white/70 leading-relaxed">Rekisa will review your requirements and contact you about the next steps.</p>
    </div>
  );
}
